<template>
	<div class="company-list px-5">
		<app-table compact contained sticky-header class="companies-table is-not-selectable" name="companies"
			:columns="columns"
			:column-widths="{ 1: '19%', 2: '8%', 3: '10%', 4: '20%', 5: '11%', 6: '7%', 7: '9%', 8: '8%', 9: '8%' }"
			:current-page="currentPage" :rows="rows" :last-page="lastPage" :search-value="searchValue"
			@on-search-event="onSearch" @on-page="changePage">
			<template slot="rows" slot-scope="props">
				<td class="company-name-column" v-show="showRow(columns, 1)">
					<div class="company-identity">
						<span class="company-avatar">{{ initials(props.row.nombreComercial) }}</span>
						<div class="company-primary-data">
							<strong :title="props.row.nombreComercial">{{ props.row.nombreComercial }}</strong>
							<small :title="props.row.razonSocial">{{ props.row.razonSocial || 'Sin razón social' }}</small>
						</div>
					</div>
				</td>
				<td class="company-code-column" v-show="showRow(columns, 2)">
					<app-tooltip-button color="primary" icon="content_copy" text="Copiar token" @click-icon="copyToken(props.row.hash)" />
					<strong>{{ props.row.codeCompany || '—' }}</strong>
				</td>
				<td class="company-ruc-column" v-show="showRow(columns, 3)"><strong>{{ props.row.ruc || '—' }}</strong></td>
				<td class="company-configuration-column" v-show="showRow(columns, 4)">
					<strong>{{ countryName(props.row) }}</strong>
					<small :title="templateName(props.row)">{{ templateName(props.row) }}</small>
				</td>
				<td class="company-plan-column" v-show="showRow(columns, 5)"><strong>{{ planName(props.row) }}</strong><small>{{ planEndDate(props.row) }}</small></td>
				<td class="company-languages-column" v-show="showRow(columns, 6)">
					<div class="language-chips"><span v-for="language in selectedLanguages(props.row)" :key="language">{{ language }}</span><small v-if="!selectedLanguages(props.row).length">—</small></div>
					<small>{{ selectedLanguages(props.row).length ? 'Habilitados' : 'Sin idiomas' }}</small>
				</td>
				<td class="company-services-column" v-show="showRow(columns, 7)">
					<small>{{ integrationsCount(props.row) }} integraciones</small>
					<span :class="['access-status', { restricted: props.row.flagAllowAccess }]">{{ props.row.flagAllowAccess ? 'Acceso restringido' : 'Acceso normal' }}</span>
				</td>
				<td class="company-updated-column" v-show="showRow(columns, 8)"><strong>{{ formattedUpdatedAt(props.row) }}</strong><small>Template actualizado</small></td>
				<td class="company-actions-cell"><div class="company-actions">
					<app-tooltip-button color="green" icon="edit" text="Editar Compañía" @click-icon="edit(props.row)" />
					<app-tooltip-button color="orange" icon="description" text="Abrir Template" @click-icon="openTemplate(props.row)" />
					<app-tooltip-button color="primary" icon="people_outline" text="Usuarios" @click-icon="goToUsers(props.row)" />
					<app-tooltip-button color="red" icon="playlist_add_check" text="Roles" @click-icon="openRoles(props.row)" />
					<app-tooltip-button color="green" icon="share" text="Integraciones" @click-icon="openIntegrations(props.row)" />
				</div></td>
			</template>
		</app-table>
	</div>
</template>

<script>
import appTooltipButton from '@/components/shared/app-tooltip-button';

function changePage(page) { this.$emit('change-page', page); }
function initials(name = '') {
	return name.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map(word => word[0])
		.join('')
		.toUpperCase() || 'CO';
}
function countryName(row) { return row.country && row.country.data ? row.country.data.name : 'Sin país'; }
function templateName(row) {
	const name = row.template && row.template.data ? row.template.data.name : '';
	return name ? name.replace(/\*+/g, '').trim() : 'Sin template';
}
function planName(row) {
	const plans = row.selectPlans;
	if (Array.isArray(plans)) return plans.length && plans[0].name ? plans[0].name : 'Sin plan';
	return plans && plans.name ? plans.name : 'Sin plan';
}
function planEndDate(row) {
	const plans = row.selectPlans;
	const plan = Array.isArray(plans) ? plans[0] : plans;
	if (!plan || !plan.end_date) return 'Sin vigencia';
	const date = new Date(plan.end_date.replace(' ', 'T'));
	if (Number.isNaN(date.getTime())) return plan.end_date;
	return `Vence ${date.toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric' })}`;
}
function selectedLanguages(row) {
	return Object.keys(row.languages || {})
		.filter(key => row.languages[key] && row.languages[key].isSelected)
		.map(key => (row.languages[key].id || key).toUpperCase());
}
function integrationsCount(row) {
	const services = row.settings && Array.isArray(row.settings.serviceExternal) ?
		row.settings.serviceExternal : [];
	return services.filter(service => service.flagSelected !== false).length;
}
function formattedUpdatedAt(row) {
	const value = row.template && row.template.data ? row.template.data.updatedAt : null;
	if (!value) return 'Sin fecha';
	const date = new Date(value.replace(' ', 'T'));
	if (Number.isNaN(date.getTime())) return value;
	return date.toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric' });
}
function legacyCopy(value) {
	const input = document.createElement('textarea');
	input.value = value;
	input.setAttribute('readonly', '');
	input.style.position = 'fixed';
	input.style.opacity = '0';
	document.body.appendChild(input);
	input.select();
	document.execCommand('copy');
	document.body.removeChild(input);
}
async function copyToken(token) {
	if (!token) { this.showNotification('Esta compañía no tiene token', 'warning'); return; }
	try {
		if (navigator.clipboard && navigator.clipboard.writeText) {
			await navigator.clipboard.writeText(token);
		} else {
			legacyCopy(token);
		}
		this.showNotification('Token copiado');
	} catch (error) {
		legacyCopy(token);
		this.showNotification('Token copiado');
	}
}
function edit({ id, nombreComercial, codeCompany }) {
	this.goTo('page-edit-company', { params: { idCompany: id, codeCompany, nameCompany: nombreComercial } });
}
function onSearch(value) { this.$emit('on-search', value); }
function openRoles({ codeCompany }) { this.goTo('page-role', { params: { codeCompany, codeProject: this.codeProject } }); }
function goToUsers({ codeCompany }) { this.goTo('page-user', { params: { codeProject: this.codeProject, idCompany: codeCompany } }); }
function openIntegrations({ codeCompany }) { this.goTo('page-company-integration', { params: { codeCompany, codeProject: this.codeProject } }); }
function openTemplate(row) {
	const template = row.template && row.template.data;
	const codeTemplate = template && template.codeTemplate;
	if (!codeTemplate) {
		this.showNotification('Esta compañía no tiene un template asignado', 'warning');
		return;
	}
	this.goTo('page-template', {
		params: { codeProject: this.codeProject },
		query: { search: codeTemplate },
	});
}

export default {
	name: 'companies-list',
	components: { appTooltipButton },
	props: {
		codeProject: { type: String, required: true },
		columns: { type: Array, required: true },
		currentPage: { type: Number, default: 0 },
		lastPage: { type: Number, default: 0 },
		rows: { type: Array, required: true },
		searchValue: { type: String, default: '' },
	},
	methods: {
		changePage,
		copyToken,
		countryName,
		edit,
		formattedUpdatedAt,
		goToUsers,
		initials,
		integrationsCount,
		onSearch,
		openIntegrations,
		openRoles,
		openTemplate,
		planEndDate,
		planName,
		selectedLanguages,
		templateName,
	},
};
</script>

<style lang="scss" scoped>
.company-list { padding-bottom: 70px; }
.company-actions { align-items: center; display: flex; justify-content: center; white-space: nowrap; }
.company-actions-cell { min-width: 0; padding-left: 4px !important; padding-right: 4px !important; }
.company-name-column { padding-left: 12px !important; text-align: left !important; }
.company-identity { align-items: center; display: flex; gap: 7px; min-width: 0; }
.company-primary-data { display: flex; flex: 1 1 auto; flex-direction: column; min-width: 0; }
.company-identity strong { color: #29435b; font-size: 11px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.company-identity small { color: #8494a3; font-size: 9px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.company-avatar { align-items: center; background: #e5f2fc; border-radius: 7px; color: #1875c4; display: flex; flex: 0 0 28px; font-size: 9px; font-weight: 700; height: 28px; justify-content: center; }
.company-code-column { align-items: center; white-space: nowrap; }
.company-code-column strong, .company-ruc-column strong { color: #40586e; font-size: 9px; }
.company-code-column > .btn { height: 25px !important; margin: 0 0 0 4px; min-width: 25px !important; width: 25px; }
.company-ruc-column { white-space: nowrap; }
.company-configuration-column small, .company-plan-column small, .company-languages-column > small, .company-updated-column small { color: #8a9baa; display: block; font-size: 8px; }
.company-configuration-column, .company-plan-column, .company-languages-column, .company-services-column, .company-updated-column { text-align: left !important; }
.company-configuration-column > strong { color: #344f67; display: block; font-size: 10px; }
.company-configuration-column > small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.company-plan-column > strong { color: #3576a9; display: block; font-size: 9px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.language-chips { align-items: center; display: flex; gap: 3px; margin-bottom: 3px; }
.language-chips span { background: #e7f4ed; border-radius: 5px; color: #258457; font-size: 8px; font-weight: 700; padding: 2px 5px; }
.company-services-column > small { color: #71879a; font-size: 8px; }
.access-status { color: #458362; display: block; font-size: 8px; margin-top: 2px; }
.access-status.restricted { color: #d16a3c; }
.company-updated-column strong { color: #425d73; display: block; font-size: 10px; }
@media (max-width: 700px) { .company-list { padding-left: 12px !important; padding-right: 12px !important; } }
</style>

<style lang="scss">
.companies-table .table-list {
	table-layout: fixed;
	> tbody > tr { height: 44px !important; min-height: 44px !important; }
	> tbody > tr > td { height: 44px !important; padding-bottom: 3px !important; padding-top: 3px !important; }
	.company-actions .btn { margin-left: 1px; margin-right: 1px; min-width: 27px; width: 27px; }
	.company-actions .btn { height: 27px !important; min-height: 27px !important; }
}
</style>



// WEBPACK FOOTER //
// src/components/companies/company-list.vue